import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Avatar, LogOutIcon, NavTabs } from '@novaquantis/design-system';
import type { NavTab } from '@novaquantis/design-system';
import { useAuth } from '../contexts/AuthContext';
import { useRetailAccounts as useRetailAccountsQuery } from '../queries/retailAccounts';
import { hasActivePortfolio } from '../hooks/useRetailAccounts';
import BrandMark from './BrandMark';

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Shares the cache with portfolio pages — no extra network request on navigation
  const { data: accounts = [] } = useRetailAccountsQuery({ enabled: !!user });
  const showPortfolioNav = !!user && hasActivePortfolio(accounts);

  if (
    !user ||
    location.pathname === '/login' ||
    location.pathname.startsWith('/user/invite/') ||
    location.pathname.startsWith('/onboarding')
  ) {
    return null;
  }

  const displayName = (() => {
    const firstName = (user as { firstName?: string }).firstName;
    if (firstName?.trim()) return firstName.trim();

    const fullName = ((user as { name?: string }).name || '').trim();
    if (fullName) return fullName.split(/\s+/)[0];

    const username = user?.username || '';
    const alphaPrefix = username.match(/^[A-Za-z]+/)?.[0];
    return alphaPrefix || username;
  })();

  const tabs: NavTab[] = [
    ...(showPortfolioNav ? [{ id: 'portfolio', label: 'Portfolio' }] : []),
    { id: 'simulator', label: 'Simulator' },
    { id: 'profile', label: 'Profile' },
  ];

  const activeId = (() => {
    if (location.pathname.startsWith('/portfolio')) return 'portfolio';
    if (location.pathname.startsWith('/simulator')) return 'simulator';
    if (location.pathname.startsWith('/profile')) return 'profile';
    return showPortfolioNav ? 'portfolio' : 'simulator';
  })();

  const handleTabChange = (id: string) => {
    if (id === 'portfolio') navigate('/portfolio');
    else if (id === 'simulator') navigate('/simulator');
    else navigate('/profile');
  };

  return (
    <header className="sticky top-0 z-[100] w-full flex-shrink-0 border-b border-[color:var(--nq-line-soft)] bg-[rgba(10,14,24,0.92)] backdrop-blur-[16px] [-webkit-backdrop-filter:blur(16px)]">
      <div className="mx-auto flex h-[var(--header-height)] max-w-[var(--content-max)] items-center justify-between gap-4 px-6 max-md:h-[var(--header-height-mobile)] max-md:flex-wrap max-md:px-4">
        <Link to="/" className="inline-flex items-center gap-2 text-inherit no-underline" aria-label="Home">
          <BrandMark wordmarkSize={22} logoClassName="h-8 w-8 shrink-0 object-contain" />
        </Link>

        <nav className="flex items-center gap-4">
          <NavTabs tabs={tabs} activeId={activeId} onTabChange={handleTabChange} />

          <div className="flex items-center gap-2.5">
            <Avatar name={displayName} size={32} />
            <span className="text-sm font-medium text-white/85 max-md:hidden">{displayName}</span>
            <button
              onClick={logout}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-solid border-transparent bg-transparent text-white/45 transition-[color,background,box-shadow] duration-150 hover:border-[color:var(--border-card)] hover:bg-[color:var(--nq-surface-raised)] hover:text-white/85"
              title="Logout"
              type="button"
              aria-label="Logout"
            >
              <LogOutIcon />
            </button>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
